import React, { useState } from "react";
import { FaGithub, FaLayerGroup, FaShieldAlt, FaCode } from "react-icons/fa";
import { FiExternalLink } from "react-icons/fi";

const realProjects = [
  {
    title: "RoadRescue",
    subtitle: "Roadside Assistance Coordination Platform",
    role: "Full-Stack Developer",
    date: "2025 – Present",
    track: "development",
    trackLabel: "Development",
    stack: ["Next.js", "Supabase", "PostgreSQL", "PostGIS", "Leaflet Maps", "RLS"],
    bullets: [
      "Architected a high-availability, multi-role web platform connecting stranded drivers to verified nearby mechanics using real-time location data, reducing simulated emergency response discovery time.",
      "Implemented Supabase Realtime subscriptions enabling live dispatch tracking and in-request chat between drivers and mechanics without polling overhead.",
  ],
    repo: "https://github.com/Handson-A/roadrescue"
  },
  {
    title: "StudyBridge",
    subtitle: "AI Note-Taking & Summarisation Engine",
    role: "Creator & Lead Architect",
    date: "2025",
    track: "development",
    trackLabel: "Development & AI",
    stack: ["Progressive Web App (PWA)", "Service Workers", "AI Integration", "Offline Cache"],
    bullets: [
      "Designed and built an AI-powered lecture optimisation tool structured as a Progressive Web App (PWA) for cross-platform mobile and desktop accessibility.",
      "Implemented advanced service-worker mechanics and responsive caching strategies delivering reliable offline functionality across diverse mobile platforms."
    ],
    repo: "https://github.com/Handson-A/studybridge"
  },
  {
    title: "Campus-Mart",
    subtitle: "Modular Student Marketplace",
    role: "Project Lead & Backend Developer",
    date: "2024 – 2025",
    track: "development",
    trackLabel: "Full-Stack & Agile",
    stack: ["MERN Stack", "MongoDB", "Express", "React", "Node.js", "Jira", "Agile/Scrum"],
    bullets: [
      "Directed an 8-member cross-functional engineering and design team using Agile/Scrum methodology and Jira for sprint tracking, delivering the platform on schedule and within scope.",
      "Architected modular, scalable components ensuring platform security, data integrity, and verified access restricted to university student demographics."
    ],
    repo: "https://github.com/Handson-A/campus-mart"
  },
  {
    title: "Simple Budget Tracker App",
    subtitle: "A finance coach + AI integration, for students",
    role: "Full-Stack Developer",
    date: "2025",
    track: "development",
    trackLabel: "Development",
    stack: ["MERN Stack", "PostgreSQL", "AI Integration", "Mobile-Friendly"],
    bullets: [
      "Mobile-friendly app built to support students who find it difficult to keep track of their spending.",
      "Uses the MERN stack with a PostgreSQL database server, eliminating the need for complex, heavy software."
    ],
    repo: "https://github.com/Handson-A/S_B_T"
  },
  {
    title: "Supply Chain Management System Frontend",
    subtitle: "Inventory and Logistics Tracking Interface",
    role: "Frontend Developer",
    date: "2025",
    track: "development",
    trackLabel: "Frontend",
    stack: ["React.js", "JavaScript", "CSS3", "REST APIs"],
    bullets: [
      "Developed a responsive React and JavaScript-based system for tracking inventory, operations, and logistics flows with modular component architecture."
    ],
    repo: "https://github.com/Handson-A"
  },
  {
    title: "Campus Map Project (GUI)",
    subtitle: "Interactive Campus Navigation Interface",
    role: "GUI Developer",
    date: "2024",
    track: "development",
    trackLabel: "Software Engineering",
    stack: ["Java", "Java Swing", "UI/UX Design", "Team Collaboration"],
    bullets: [
      "Designed an interactive Java Swing campus map interface focused on UI/UX clarity and component layout efficiency within a structured 5-person team environment."
    ],
    repo: "https://github.com/Handson-A"
  }
];

const conceptualLabs = [
  {
    title: "Secure Web App Lab",
    stack: "React, Node.js, Express, Helmet, JWT",
    summary: "A lab focused on secure-by-design web application patterns and automated security checks.",
    repo: "https://github.com/Handson-A"
  },
  {
    title: "Pentest Workshop",
    stack: "Kali Linux, Nmap, Burp Suite, Custom Scripts",
    summary: "Collection of exercises and proof-of-concepts demonstrating common vulnerabilities and mitigations.",
    repo: "https://github.com/Handson-A"
  },
  {
    title: "Test Automation Suite",
    stack: "Cypress, Jest, GitHub Actions, Node.js",
    summary: "Sample test suites and CI pipelines that emphasize reliability and fast feedback loops.",
    repo: "https://github.com/Handson-A"
  }
];

function Projects() {
  const [filter, setFilter] = useState("all");

  const filteredProjects = realProjects.filter((p) => {
    if (filter === "all") return true;
    return p.track === filter;
  });

  return (
    <section id="projects" className="projects">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">Projects & Applications</h2>
          <p className="section-subtitle">
            Production-grade systems, full-stack applications, and architectural builds designed with reliability and security in mind.
          </p>
        </div>

        {/* Filter Chips */}
        <div className="filter-container">
          <span className="filter-label">Filter Track:</span>
          <button
            className={`filter-chip ${filter === "all" ? "active" : ""}`}
            onClick={() => setFilter("all")}
          >
            <FaLayerGroup /> All Projects ({realProjects.length})
          </button>
          <button
            className={`filter-chip ${filter === "development" ? "active" : ""}`}
            onClick={() => setFilter("development")}
          >
            <FaCode /> Software Development ({realProjects.length})
          </button>
          <button
            className={`filter-chip ${filter === "infrastructure" ? "active" : ""}`}
            onClick={() => setFilter("infrastructure")}
          >
            <FaShieldAlt /> Infrastructure & Security
          </button>
        </div>

        {/* Real Projects Grid */}
        <div className="project-grid">
          {filteredProjects.length > 0 ? (
            filteredProjects.map((p, i) => (
              <article key={i} className="project-card">
                <div>
                  <div className="project-header">
                    <h3 className="project-title">{p.title}</h3>
                    <span className="project-track-badge">{p.trackLabel}</span>
                  </div>
                  <div className="project-role-badge">
                    {p.role} • {p.date}
                  </div>
                  <ul className="project-bullets">
                    {p.bullets.map((b, j) => (
                      <li key={j}>{b}</li>
                    ))}
                  </ul>
                </div>

                <div className="project-footer">
                  <div className="project-stack">
                    {p.stack.map((tech, k) => (
                      <span key={k} className="stack-tag">
                        {tech}
                      </span>
                    ))}
                  </div>
                  <div style={{ marginTop: "14px" }}>
                    <a
                      href={p.repo}
                      target="_blank"
                      rel="noreferrer"
                      className="repo-link"
                    >
                      <FaGithub />
                      <span>Explore Repository</span>
                      <FiExternalLink size={14} />
                    </a>
                  </div>
                </div>
              </article>
            ))
          ) : (
            <div style={{ color: "var(--muted)", padding: "20px" }}>
              Explore hands-on infrastructure exercises in the Conceptual Labs section below.
            </div>
          )}
        </div>

        {/* Conceptual Labs Subsection */}
        <div className="labs-subhead">
          <div className="labs-title">
            <span>Conceptual Labs - pipeline</span>
            <span className="lab-badge">Learning & Practice Work</span>
          </div>
          <p className="labs-desc">
            Focused technical sandboxes and experimental prototypes exploring vulnerability analysis, security tooling, and quality assurance suites.
          </p>
        </div>

        <div className="project-grid">
          {conceptualLabs.map((lab, i) => (
            <article key={i} className="project-card">
              <div>
                <div className="project-header">
                  <h3 className="project-title">{lab.title}</h3>
                  <span className="lab-badge">Sandbox</span>
                </div>
                <p
                  style={{
                    color: "var(--text-lead)",
                    fontSize: "0.925rem",
                    margin: "10px 0 14px 0",
                    lineHeight: "1.6"
                  }}
                >
                  {lab.summary}
                </p>
              </div>

              <div className="project-footer">
                <div className="project-stack">
                  {lab.stack.split(", ").map((tech, k) => (
                    <span key={k} className="stack-tag">
                      {tech}
                    </span>
                  ))}
                </div>
                <div style={{ marginTop: "14px" }}>
                  <a
                    href={lab.repo}
                    target="_blank"
                    rel="noreferrer"
                    className="repo-link"
                  >
                    <FaGithub />
                    <span>Explore Repository</span>
                    <FiExternalLink size={14} />
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export default Projects;

